import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Stack, Theme, Typography } from '@mui/material';

import dayjs from 'dayjs';

import { ShipmentResponse } from '@/api/data-contracts';
import { ReactComponent as CheckIcon } from '@/assets/icons/check-light-icon.svg';
import { ReactComponent as LinkExternalIcon } from '@/assets/icons/link-external-blue-icon.svg';
import AppButton from '@/shared/ui/AppButton';
import { FONT_FAMILY } from '@/shared/ui/form-fields/fieldStyles';

import { useShipmentTrackingQuery } from '../../../api/useShippingQueries';
import { ShipmentTimelineStep, buildShipmentTimeline } from '../../../model/allAwbs';
import { StepDot, StepDotSlot, StepItem, StepText, StepperLine, StepperTrack } from '../styles';

interface ShipmentTimelinePanelProps {
  shipment: ShipmentResponse;
}

const STEP_LABELS: Record<ShipmentTimelineStep['key'], string> = {
  created: 'awb.timeline_created',
  pickedUp: 'awb.timeline_picked_up',
  delivered: 'awb.timeline_delivered',
  cancelled: 'awb.timeline_cancelled',
};

const CANCELLED_DOT_SX = {
  backgroundColor: (theme: Theme) => theme.palette.red[500],
  boxShadow: (theme: Theme) => `0 0 0 4px ${theme.palette.red[200]}`,
};

const SKIPPED_DOT_SX = {
  borderColor: (theme: Theme) => theme.palette.grey[300],
};

const SKIPPED_TEXT_SX = {
  color: (theme: Theme) => theme.palette.grey[400],
};

const STEP_AT_SX = {
  fontFamily: FONT_FAMILY,
  fontSize: '0.625rem',
  fontStyle: 'normal',
  fontWeight: 350,
  lineHeight: '125%',
  letterSpacing: 0,
  color: (theme: Theme) => theme.palette.grey[950],
  whiteSpace: 'nowrap',
};

const ShipmentTimelinePanel: FC<ShipmentTimelinePanelProps> = ({ shipment }) => {
  const { t } = useTranslation();
  const { data: events = [] } = useShipmentTrackingQuery(shipment.id);

  const steps = useMemo(() => buildShipmentTimeline(shipment, events), [events, shipment]);
  const cancelled = steps.some((step) => step.state === 'cancelled');

  const formatAt = (at: string | undefined) => {
    if (!at) return '-';
    const moment = dayjs(at);
    return moment.isSame(dayjs(), 'day')
      ? t('awb.today_at', { time: moment.format('HH:mm') })
      : moment.format('D MMM, HH:mm');
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="1.5rem"
      sx={{ padding: '1rem 1rem 1rem 6.5rem', borderBottom: '1px solid', borderColor: 'grey.100' }}
      data-testid="awb-timeline"
    >
      <StepperTrack sx={{ maxWidth: 'none', flex: 1, margin: 0 }}>
        <StepperLine />
        {steps.map((step) => {
          const skipped = cancelled && step.state === 'todo';
          return (
            <StepItem key={step.key} data-testid={`awb-timeline-${step.key}`}>
              <StepDotSlot>
                <StepDot
                  state={
                    step.state === 'todo' ? 'todo' : step.state === 'current' ? 'active' : 'done'
                  }
                  sx={
                    step.state === 'cancelled'
                      ? CANCELLED_DOT_SX
                      : skipped
                        ? SKIPPED_DOT_SX
                        : undefined
                  }
                >
                  {step.state === 'done' && <CheckIcon />}
                </StepDot>
              </StepDotSlot>
              <StepText sx={skipped ? SKIPPED_TEXT_SX : undefined}>
                {t(STEP_LABELS[step.key])}
              </StepText>
              <Typography sx={skipped ? [STEP_AT_SX, SKIPPED_TEXT_SX] : STEP_AT_SX}>
                {formatAt(step.at)}
              </Typography>
            </StepItem>
          );
        })}
      </StepperTrack>

      {!!shipment.trackingUrl && (
        <AppButton
          variant="secondary"
          size="small"
          endIcon={<LinkExternalIcon />}
          href={shipment.trackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="awb-timeline-track"
        >
          {t('Track parcel')}
        </AppButton>
      )}
    </Stack>
  );
};

export default ShipmentTimelinePanel;
